import { useMemo } from 'react';

import { Button } from '../../shared/ui/button';
import { CopyField } from '../../shared/ui/copy-field';
import { Note } from '../../shared/ui/note';
import { PanelEmpty, PanelFoot, SectionPanel } from '../../shared/ui/section-panel';
import { Table, type IColumn } from '../../shared/ui/table';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { cn } from '../../shared/lib/cn';
import {
  invitationDeadline,
  isManager,
  roleLabel,
  type IActor,
  type TInvitation,
} from '../../entities/organization';
import type { TMembers } from './use-members';

interface IInvitationsPanelProps {
  members: TMembers;
  actor: IActor | null;
  timeZone?: string;
  onInvite?: () => void;
}

export const InvitationsPanel = ({
  members,
  actor,
  timeZone,
  onInvite,
}: IInvitationsPanelProps) => {
  const rows = members.invitations.data ?? [];
  const now = useMemo(() => Date.now(), [rows]);
  const manager = isManager(actor);

  const columns: IColumn<TInvitation>[] = [
    { key: 'email', header: 'Email', width: 'grow', render: (row) => row.email },
    {
      key: 'role',
      header: 'Role',
      width: 'w-col-role',
      ui: true,
      render: (row) => roleLabel(row.role),
    },
    {
      key: 'expires',
      header: 'Link',
      width: 'w-col-status',
      ui: true,
      render: (row) => {
        const deadline = invitationDeadline(row, now, timeZone);
        return (
          <span className="flex items-center gap-2">
            <span
              aria-hidden
              className={cn(
                'size-dot shrink-0 rounded-full',
                deadline.expired ? 'bg-faint' : 'bg-motion',
              )}
            />
            <span className="truncate">{deadline.label}</span>
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      width: 'w-col-row-actions',
      ui: true,
      render: (row) =>
        manager && (
          <span className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              waiting={members.resend.isPending && members.resend.variables?.id === row.id}
              waitingLabel="Sending"
              onClick={() => members.resend.mutate(row)}
            >
              Send again
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={members.revoke.isPending}
              onClick={() => members.revoke.mutate(row.id)}
            >
              Cancel
            </Button>
          </span>
        ),
    },
  ];

  return (
    <SectionPanel title="Invitations" note={`${rows.length} waiting to be accepted`}>
      {members.handLink && (
        <div className="flex flex-col gap-2 p-input">
          <Note tone="caution">
            Email isn’t set up here — send this link yourself. It works once, until the invitation
            expires, and this is the only time it is shown.
          </Note>
          <CopyField
            value={members.handLink.url}
            label={`Invitation link for ${members.handLink.email}`}
          />
          <span>
            <Button variant="ghost" size="sm" onClick={members.dismissHandLink}>
              Hide link
            </Button>
          </span>
        </div>
      )}
      {members.invitations.isError && (
        <div className="p-input">
          <ErrorState
            error={members.invitations.error}
            onRetry={() => void members.invitations.refetch()}
          />
        </div>
      )}
      {members.invitations.isPending && <LoadingState rows={2} className="p-input" />}

      {members.invitations.isSuccess && rows.length === 0 && (
        <PanelEmpty
          title="Nobody is waiting to join"
          action={
            manager &&
            onInvite && (
              <Button variant="secondary" size="sm" onClick={onInvite}>
                Invite people
              </Button>
            )
          }
        >
          Invite a teammate by email. They get a link that works for a few days.
        </PanelEmpty>
      )}

      {rows.length > 0 && (
        <>
          <Table
            height="xl"
            layoutId="settings-invitations"
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
          />
          {manager && (
            <PanelFoot>
              Send again replaces the link: the one sent before stops working. Cancel takes the
              invitation back before it is used.
            </PanelFoot>
          )}
        </>
      )}
    </SectionPanel>
  );
};
